import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Tag,
  Typography,
  Table,
  Input,
  Space,
  Dropdown,
  Button,
  App,
  Tooltip,
} from 'antd';
import {
  MoreOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  LockOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import {
  deleteModality,
  listModalities,
  updateModalityStatus,
} from '../../services/modalities';
import { Avatar } from 'antd';
import { Image } from 'antd';
import { resolveMedia } from '../../utils/media';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;
const { useApp } = App;

const ModalitiesList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { can } = usePermissions();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  const fetchData = useCallback(
    async (page, pageSize, srt, q) => {
      setLoading(true);
      try {
        const { items, total } = await listModalities({
          page: page || pagination.current,
          pageSize: pageSize || pagination.pageSize,
          search: q || undefined,
          sortField: srt?.field,
          sortOrder: srt?.order,
        });
        setData(items || []);
        setPagination((prev) => ({
          ...prev,
          current: page || prev.current,
          pageSize: pageSize || prev.pageSize,
          total,
        }));
      } catch (err) {
        message.error('No se pudo cargar la lista de regiones.');
      } finally {
        setLoading(false);
      }
    },
    [pagination, message],
  );

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    // al buscar, reinicia a página 1
    fetchData(1, pagination.pageSize, sorter, value || '');
  };

  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    switch (action) {
      case 'edit':
        navigate(`/modalities/${record.id}/edit`);
        break;
      case 'toggle':
        const willActivate = record.is_deleted;
        modal.confirm({
          title: willActivate
            ? '¿Activar modalidad?'
            : '¿Desactivar modalidad?',
          content: `${record.description || 'modalidad'}`,
          okText: willActivate ? 'Activar' : 'Desactivar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await updateModalityStatus(record.id, willActivate);
              message.success(
                willActivate ? 'modalidad activada' : 'modalidad desactivada',
              );
              fetchData(
                pagination.current,
                pagination.pageSize,
                sorter,
                search,
              );
            } catch {
              message.error('No se pudo actualizar el estatus');
            }
          },
        });
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar modalidad?',
          content: `${record.description || 'Modalidad'}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deleteModality(record.id);
              message.success('Modalidad eliminada');

              const nextPage =
                data.length === 1 && pagination.current > 1
                  ? pagination.current - 1
                  : pagination.current;
              setPagination((prev) => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search);
            } catch (error) {
              message.error('No se pudo eliminar la modalidad');
            }
          },
        });
        break;
      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const menuItems = [
      can('modalities.edit') && {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar modalidad',
      },
      can('modalities.toggle') && {
        key: 'toggle',
        icon: <LockOutlined />,
        label: !record.is_deleted ? 'Desactivar' : 'Activar',
      },
      {
        type: 'divider',
      },
      can('modalities.delete') && {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Borrar modalidad',
        danger: true,
      },
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    {
      title: 'Icono',
      dataIndex: 'icon',
      key: 'image',
      width: 96,
      align: 'center',
      render: (_, r) => {
        const mediaPath = r.icon || r.media_path;
        const src = resolveMedia(mediaPath);
        const label = r.code || 'Modalidad';
        if (!src) {
          return <Avatar shape="square" size={64} icon={<PictureOutlined />} />;
        }
        return (
          <Image
            src={src}
            alt={label}
            width={64}
            height={64}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            onClick={(e) => e.stopPropagation()}
            preview={{ mask: 'Ver' }}
            fallback="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64'/>"
          />
        );
      },
    },
    { title: 'Clave', dataIndex: 'code', key: 'code', sorter: true },
    { title: 'Modalidad', dataIndex: 'name', key: 'name', sorter: true },
    {
      title: 'Descripción corta',
      dataIndex: 'description',
      key: 'description',
      sorter: true,
    },
    {
      title: 'Estado',
      dataIndex: 'is_deleted',
      key: 'is_deleted',
      sorter: true,
      render: (isDeleted) => (
        <Tag color={!isDeleted ? 'green' : 'volcano'}>
          {!isDeleted ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    { title: 'Orden', dataIndex: 'order', key: 'order', sorter: true },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown
          menu={getActionsMenu(record)}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
        <Title level={3} style={{ marginBottom: 0 }}>
          Modalidades
        </Title>
        {can('modalities.add') && (
          <Tooltip title="Agregar Modalidad">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="btn-add"
              aria-label="Agregar modalidad"
              onClick={() => navigate('/modalities/add')}
            />
          </Tooltip>
        )}
      </div>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder="Región"
            allowClear
            onSearch={onSearch}
            style={{ maxWidth: 360 }}
          />
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total) => `Total: ${total} modalidades`,
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default ModalitiesList;
